import type { RenderParams } from '@seek/forms-ui';
import { isEmail, maxLength, required } from '@seek/validators-js';
import {
  Button,
  Card,
  Inline,
  RadioGroup,
  RadioItem,
  Stack,
  Text,
  TextField,
  TextLink,
} from 'braid-design-system';

import { UserRegistrationType } from 'src/generated/graphql';
import { mapBraidFieldProps } from 'src/utils/mapBraidFieldProps';

import { type FormValues, useField } from '../MarketplaceInsights';

const DEFAULT_CHARACTER_LIMIT = 250;

interface Props extends RenderParams<FormValues> {
  onSubmit: (formData: FormValues) => void;
  loading: boolean;
}

export const SignupForm = (props: Props) => {
  const firstNameField = useField({
    id: 'firstName',
    validators: [required, maxLength],
    validatorProps: {
      requiredLabel: ' your first name.',
      max: DEFAULT_CHARACTER_LIMIT,
    },
  });
  const lastNameField = useField({
    id: 'lastName',
    validators: [required, maxLength],
    validatorProps: {
      requiredLabel: ' your last name.',
      max: DEFAULT_CHARACTER_LIMIT,
    },
  });
  const emailField = useField({
    id: 'email',
    validators: [required, maxLength, isEmail],
    validatorProps: {
      requiredLabel: ' your email.',
      max: DEFAULT_CHARACTER_LIMIT,
    },
  });
  const userTypeField = useField({
    id: 'userType',
    validators: [required],
    validatorProps: {
      requiredMessage: 'Please select from the following.',
    },
  });

  return (
    <Card>
      <form onSubmit={props.handleSubmit(props.onSubmit)}>
        <Stack space="xlarge">
          <Stack space="medium">
            <TextField
              name="fields[first_name]"
              id="drip-first-name"
              label="First name"
              characterLimit={DEFAULT_CHARACTER_LIMIT}
              {...mapBraidFieldProps(firstNameField)}
            />

            <TextField
              name="fields[last_name]"
              id="drip-last-name"
              label="Last name"
              characterLimit={DEFAULT_CHARACTER_LIMIT}
              {...mapBraidFieldProps(lastNameField)}
            />

            <TextField
              name="fields[email]"
              id="drip-email"
              label="Email address"
              characterLimit={DEFAULT_CHARACTER_LIMIT}
              {...mapBraidFieldProps(emailField)}
            />

            <RadioGroup
              name="fields[insights_report_signup]"
              id="drip-insights-report-signup"
              label="Select from the following"
              {...mapBraidFieldProps(userTypeField)}
            >
              <RadioItem
                label="I am a Buyer"
                value={UserRegistrationType.Buyer}
              />
              <RadioItem
                label="I am a Private Seller"
                value={UserRegistrationType.PrivateSeller}
              />
              <RadioItem
                label="I am a Broker"
                value={UserRegistrationType.Broker}
              />
              <RadioItem
                label="I am a Franchisor"
                value={UserRegistrationType.Franchisor}
              />
              <RadioItem
                label="I am a Real Estate Agent"
                value={UserRegistrationType.RealEstateAgent}
              />
              <RadioItem
                label="I am a small to medium-sized enterprise (SME)"
                value={UserRegistrationType.Other}
              />
            </RadioGroup>
          </Stack>

          <Stack space="medium">
            <Inline space="none">
              <Button
                data={{ 'data-drip-attribute': 'sign-up-button' }}
                tone="formAccent"
                type="submit"
                loading={props.loading}
              >
                Get your free report
              </Button>
            </Inline>

            <Text size="small" tone="secondary">
              By providing your personal information, you agree to our{' '}
              <TextLink
                href="https://www.seekbusiness.com.au/collection-notice"
                target="_blank"
              >
                Collection Notice
              </TextLink>{' '}
              and{' '}
              <TextLink
                href="https://www.seekbusiness.com.au/privacy-policy"
                target="_blank"
              >
                Privacy Policy
              </TextLink>
              .
            </Text>
          </Stack>
        </Stack>
      </form>
    </Card>
  );
};
